/**
 * Pixel-art car sprites.
 *
 * Every silhouette is drawn once per (class, colour) into a small offscreen
 * canvas and then blitted rotated at race time. Cars are drawn nose-right in
 * sprite space so the world renderer can simply rotate by the car's heading.
 */

import type { CarClass } from '@shared/cars.js';

/** Sprite canvas is drawn at this size and scaled down onto the car. */
const SPRITE = 72;
const PX = 3; // virtual pixel size inside the sprite

export interface CarVisual {
  body: string;
  dark: string;
  light: string;
  accent: string;
}

interface SpriteSpec {
  /** Body outline in virtual pixels, drawn as a run of horizontal spans. */
  spans: Array<[number, number, number, number]>; // x, y, w, h  (virtual px)
  cabin: Array<[number, number, number, number]>;
  wheels: Array<[number, number]>;
  spoiler?: [number, number, number, number];
  stripe?: [number, number, number, number];
}

/** 16x12 virtual-pixel silhouettes (before scaling), nose pointing right. */
const SPECS: Record<string, SpriteSpec> = {
  hatch: {
    spans: [
      [2, 4, 12, 4],
      [3, 3, 10, 6],
      [1, 5, 2, 3],
    ],
    cabin: [[4, 4, 6, 4]],
    wheels: [[3, 3], [3, 9], [11, 3], [11, 9]],
    stripe: [8, 5, 4, 2],
  },
  kart: {
    spans: [
      [2, 5, 11, 2],
      [4, 4, 7, 4],
      [1, 5, 2, 2],
    ],
    cabin: [[6, 5, 3, 2]],
    wheels: [[2, 4], [2, 9], [11, 4], [11, 9]],
    spoiler: [1, 3, 2, 7],
  },
  tuner: {
    spans: [
      [1, 4, 13, 4],
      [2, 3, 11, 6],
      [0, 5, 2, 3],
    ],
    cabin: [[4, 4, 6, 4]],
    wheels: [[3, 3], [3, 9], [11, 3], [11, 9]],
    spoiler: [0, 3, 2, 8],
    stripe: [5, 4, 5, 1],
  },
  muscle: {
    spans: [
      [1, 3, 13, 6],
      [0, 5, 2, 3],
      [12, 5, 2, 2],
    ],
    cabin: [[4, 4, 5, 5]],
    wheels: [[3, 2], [3, 9], [11, 2], [11, 9]],
    spoiler: [0, 3, 2, 8],
    stripe: [5, 6, 6, 3],
  },
  rally: {
    spans: [
      [1, 4, 13, 5],
      [2, 3, 11, 7],
      [0, 5, 2, 4],
    ],
    cabin: [[4, 4, 6, 5]],
    wheels: [[3, 2], [3, 9], [11, 2], [11, 9]],
    spoiler: [0, 3, 2, 9],
    stripe: [2, 5, 2, 4],
  },
  drift: {
    spans: [
      [1, 3, 13, 6],
      [0, 5, 2, 3],
    ],
    cabin: [[4, 4, 5, 5]],
    wheels: [[3, 2], [3, 9], [11, 2], [11, 9]],
    spoiler: [0, 2, 2, 10],
    stripe: [6, 4, 4, 5],
  },
  super: {
    spans: [
      [1, 4, 13, 4],
      [3, 3, 9, 6],
      [0, 5, 2, 3],
    ],
    cabin: [[5, 4, 5, 4]],
    wheels: [[3, 3], [3, 9], [11, 3], [11, 9]],
    spoiler: [0, 3, 2, 8],
    stripe: [8, 5, 3, 3],
  },
  offroad: {
    spans: [
      [1, 3, 13, 7],
      [2, 2, 10, 8],
      [0, 5, 2, 4],
    ],
    cabin: [[3, 5, 7, 5]],
    wheels: [[2, 1], [2, 10], [11, 1], [11, 10]],
    spoiler: [0, 2, 2, 10],
    stripe: [5, 5, 4, 2],
  },
};

function shade(colour: string, factor: number): string {
  const hex = colour.replace('#', '');
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  const r = Math.min(255, Math.max(0, Math.round(parseInt(full.slice(0, 2), 16) * factor)));
  const g = Math.min(255, Math.max(0, Math.round(parseInt(full.slice(2, 4), 16) * factor)));
  const b = Math.min(255, Math.max(0, Math.round(parseInt(full.slice(4, 6), 16) * factor)));
  return `rgb(${r},${g},${b})`;
}

function specFor(sprite: CarClass | string): SpriteSpec {
  return SPECS[sprite] ?? SPECS.hatch!;
}

export function visualFor(colour: string): CarVisual {
  return {
    body: colour,
    dark: shade(colour, 0.62),
    light: shade(colour, 1.28),
    accent: '#f2f14e',
  };
}

const cache = new Map<string, HTMLCanvasElement>();

/** Returns (and caches) a nose-right sprite for a car class and colour. */
export function carSprite(sprite: CarClass | string, colour: string): HTMLCanvasElement {
  const key = `${sprite}|${colour}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const canvas = document.createElement('canvas');
  canvas.width = SPRITE;
  canvas.height = SPRITE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable');
  const spec = specFor(sprite);
  const colours = visualFor(colour);

  // The virtual drawing area is 16x12 virtual pixels; centre it in the sprite.
  ctx.save();
  ctx.translate(SPRITE / 2, SPRITE / 2);
  ctx.scale(PX, PX);
  ctx.translate(-8, -6);

  // Tyres first so the body overlaps them.
  ctx.fillStyle = '#15171b';
  for (const [wx, wy] of spec.wheels) {
    ctx.fillRect(wx, wy, 2.4, 2.2);
  }

  // Drop shadow for a touch of depth.
  ctx.globalAlpha = 0.28;
  ctx.fillStyle = '#000000';
  for (const [x, y, w, h] of spec.spans) ctx.fillRect(x, y + 0.6, w, h);
  ctx.globalAlpha = 1;

  // Body.
  ctx.fillStyle = colours.body;
  for (const [x, y, w, h] of spec.spans) ctx.fillRect(x, y, w, h);
  // Lower body shading, upper highlight.
  ctx.fillStyle = colours.dark;
  for (const [x, y, w, h] of spec.spans) ctx.fillRect(x, y + h - 1, w, 1);
  ctx.fillStyle = colours.light;
  for (const [x, y, w] of spec.spans.map((s) => [s[0], s[1], s[2]] as [number, number, number])) {
    ctx.fillRect(x, y, w, 0.6);
  }

  if (spec.stripe) {
    ctx.fillStyle = colours.dark;
    ctx.fillRect(spec.stripe[0], spec.stripe[1], spec.stripe[2], spec.stripe[3]);
  }
  if (spec.spoiler) {
    ctx.fillStyle = colours.dark;
    ctx.fillRect(spec.spoiler[0], spec.spoiler[1], spec.spoiler[2], spec.spoiler[3]);
  }

  // Glass.
  ctx.fillStyle = '#1d2b3a';
  for (const [x, y, w, h] of spec.cabin) ctx.fillRect(x, y, w, h);
  ctx.fillStyle = 'rgba(180,220,255,0.35)';
  for (const [x, y, w] of spec.cabin.map((c) => [c[0], c[1], c[2]] as [number, number, number])) {
    ctx.fillRect(x, y, w, 0.8);
  }

  // Lights: headlights forward, tail lights behind.
  ctx.fillStyle = '#fff6cf';
  ctx.fillRect(15.2, 5, 0.9, 1);
  ctx.fillRect(15.2, 8, 0.9, 1);
  ctx.fillStyle = '#ff5a4a';
  ctx.fillRect(0.4, 5, 0.9, 1);
  ctx.fillRect(0.4, 8, 0.9, 1);

  ctx.restore();
  cache.set(key, canvas);
  return canvas;
}

/**
 * Blits a car sprite into world space.
 * `length`/`width` are the physics dimensions so the art always matches the hull.
 */
export function drawCar(
  ctx: CanvasRenderingContext2D,
  sprite: CarClass | string,
  colour: string,
  x: number,
  y: number,
  angle: number,
  length: number,
  width: number,
): void {
  const image = carSprite(sprite, colour);
  // The virtual art is 16 long by 12 wide, so scale each axis independently.
  const scaleX = (length * 1.05) / 16;
  const scaleY = (width * 1.15) / 12;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(image, -8 * scaleX - scaleX / 2, -6 * scaleY - scaleY / 2, 16 * scaleX, 12 * scaleY);
  ctx.restore();
}

/** Clears the sprite cache (used when a colour is edited in the garage). */
export function clearCarSpriteCache(): void {
  cache.clear();
}
